<template>
    <div class="verify-code">
        <div v-if="loading" class="verify-code__loading">
            <div class="spinner-border">
                <span class="sr-only">{{ $t('Loading...') }}</span>
            </div>
        </div>
        <div v-else class="verify-code__content">
            <template v-if="codeVerified">
                <ui-empty-state
                    img-size="sm"
                    img="verification-check.svg"
                    :title="$t('Code verified successfully')"
                    :text="$t('Your verification code has been validated.')"
                    class="verify-code__empty-state"
                />
            </template>
            <template v-else>
                <router-link class="verify-code__back" to="/">
                    <svg width="33" height="33" viewBox="0 0 33 33" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M23.376 15.125H12.9397L17.4772 10.6013C17.7361 10.3423 17.8816 9.99118 17.8816 9.62501C17.8816 9.25885 17.7361 8.90768 17.4772 8.64876C17.2183 8.38984 16.8671 8.24438 16.501 8.24438C16.1348 8.24438 15.7836 8.38984 15.5247 8.64876L8.64971 15.5238C8.52453 15.6545 8.4264 15.8087 8.36096 15.9775C8.22343 16.3123 8.22343 16.6878 8.36096 17.0225C8.4264 17.1913 8.52453 17.3455 8.64971 17.4763L15.5247 24.3513C15.6525 24.4801 15.8046 24.5824 15.9722 24.6522C16.1397 24.722 16.3194 24.758 16.501 24.758C16.6825 24.758 16.8622 24.722 17.0298 24.6522C17.1973 24.5824 17.3494 24.4801 17.4772 24.3513C17.6061 24.2234 17.7084 24.0714 17.7782 23.9038C17.848 23.7362 17.8839 23.5565 17.8839 23.375C17.8839 23.1935 17.848 23.0138 17.7782 22.8462C17.7084 22.6787 17.6061 22.5266 17.4772 22.3988L12.9397 17.875H23.376C23.7406 17.875 24.0904 17.7301 24.3482 17.4723C24.6061 17.2144 24.751 16.8647 24.751 16.5C24.751 16.1353 24.6061 15.7856 24.3482 15.5277C24.0904 15.2699 23.7406 15.125 23.376 15.125Z" fill="#202225"/>
                    </svg>
                </router-link>
                <router-link to="/" class="verify-code__logo-link">
                    <img
                        src="@/assets/images/logos/accounts-logo.svg"
                        class="verify-code__logo img-fluid"
                        alt="Tendencys"
                    />
                </router-link>
                <div v-if="error" class="verify-code__error">
                    <div class="alert alert-danger" role="alert">
                        {{ error }}
                    </div>
                </div>
                <h1 class="verify-code__title">
                    {{ $t('Code verification') }}
                </h1>
                <p class="verify-code__text">
                    {{ $t('Enter the verification code you received to continue.') }}
                </p>
                <form @submit.prevent="handleSubmit()" class="verify-code__form">
                    <div class="verify-code__input-container">
                        <ui-input
                            id="verification-code"
                            v-model="formData.verificationCode"
                            name="verificationCode"
                            type="number"
                            placeholder="000000"
                            :label="$t('Verification code')"
                            :field="v$.formData.verificationCode"
                            :disabled="!tokenData || error"
                        />
                    </div>
                    <ui-button
                        :loading="validatingCode"
                        :disabled="!tokenData || error"
                        class="w-100 verify-code__button"
                        type="submit"
                        variant="primary"
                    >
                        {{ $t('Validate code') }}
                    </ui-button>
                </form>
            </template>
        </div>
    </div>
</template>

<script>
import { useVuelidate } from '@vuelidate/core';
import { helpers, maxLength, minLength, required } from '@vuelidate/validators';
import { decrypt } from '@tendencys/encrypter';

import UiButton from '@/components/ui/buttons/Button.vue';
import UiInput from '@/components/ui/inputs/Input.vue';
import UiEmptyState from '@/components/ui/EmptyState.vue';
import webViewMixin from '@/plugins/mixin/web-view.mixin';

export default {
    name: 'VerifyCode',
    components: {
        UiButton,
        UiInput,
        UiEmptyState,
    },
    mixins: [webViewMixin],
    setup() {
        return {
            v$: useVuelidate(),
        };
    },
    data() {
        return {
            loading: false,
            error: null,
            validatingCode: false,
            tokenData: null,
            codeVerified: false,
            redirectTimeout: null,
            formData: {
                verificationCode: '',
            },
        };
    },
    validations() {
        return {
            formData: {
                verificationCode: {
                    required: helpers.withMessage(this.$t('validations.required', { field: this.$t('code') }), required),
                    minLength: helpers.withMessage(({ $params }) => this.$t('This field must be a minimum of {min} characters', $params), minLength(6)),
                    maxLength: helpers.withMessage(({ $params }) => this.$t('This field must have a maximum of {max} characters', $params), maxLength(6)),
                    $autoDirty: true,
                },
            },
        };
    },
    async mounted() {
        const { _v } = this.$route.query;
        if (!_v) {
            this.error = 'The autorization is required.';
        } else {
            this.tokenData = JSON.parse(decrypt(_v, window.ENV.VUE_APP_TENCRYPT));
            this.axiosAccount.defaults.headers.common['Authorization'] = this.tokenData.token;
            await this.axiosAccount.get(`/api/accounts/recovery`).catch((err) => {
                this.error = 'We were unable to verify your code. Please try again.';
            });
        }
    },
    methods: {
        async handleSubmit() {
            try {
                this.v$.$touch();

                if (this.v$.$invalid) {
                    return;
                }

                if (!this.tokenData) {
                    this.error = this.$t('The authorization is required.');
                    return;
                }

                this.validatingCode = true;
                this.error = null;

                const payload = {
                    code: this.formData.verificationCode,
                };

                await this.axiosAccount.post('/api/notifications/code-verification', payload);
                
                // Mostrar empty state de éxito
                this.codeVerified = true;
            } catch (error) {
                const status = error?.response?.status;

                if (status === 404) {
                    this.error = this.$t('The code is invalid.');
                } else if (status === 429) {
                    this.error = this.$t('You have exceeded your request limit. Please try again later.');
                } else {
                    this.showError(error);
                }
            } finally {
                this.validatingCode = false;
            }
        },
    },
};
</script>

<style lang="scss">
.verify-code {
    position: relative;
    text-align: center;

    &__content {
        width: 100%;
        max-width: 500px;
        margin: 0 auto;
        position: relative;
    }

    &__back {
        position: absolute;
        left: 0;
        top: 0;
        z-index: 10;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    &__logo-link {
        display: inline-block;
        margin-bottom: 40px;
    }

    &__logo {
        max-width: 200px;
        height: auto;
    }

    &__error {
        margin-bottom: 24px;
        text-align: left;
    }

    &__title {
        font-size: 28px;
        font-weight: 600;
        margin-bottom: 12px;
        color: #202225;
        text-align: center;
    }

    &__text {
        color: #787F84;
        font-size: 16px;
        margin-bottom: 32px;
        font-weight: 400;
        text-align: center;
        line-height: 1.5;
    }

    &__form {
        text-align: left;
        max-width: 100%;
    }

    &__input-container {
        margin-bottom: 24px;
    }

    &__button {
        margin-top: 0;
    }

    &__loading {
        display: flex;
        justify-content: center;
        align-items: center;
        min-height: 400px;
    }

    &__empty-state {
        margin: 40px 0;
    }
}
</style>

